import { useState, useEffect } from "react";
import { C } from "../lib/colors";

// Promise-returning confirmation modal — drop-in replacement for the
// browser's confirm() with on-brand styling. Module-level pub/sub means
// any caller can `await confirmDialog({...})` from anywhere without
// threading a context through props.
//
// Usage:
//   const ok = await confirmDialog({
//     title: "Delete this client?",
//     message: "Both the portal account and all their content will be removed.",
//     confirmLabel: "Delete",
//     danger: true,
//   });
//   if (!ok) return;

let resolver = null;
let listener = null;

const open = (opts) => {
  if (resolver) {
    // Resolve any pending dialog as false before opening a new one so
    // promises don't dangle if the caller is fired twice.
    resolver(false);
  }
  return new Promise((resolve) => {
    resolver = resolve;
    if (listener) listener({ ...opts, open: true });
  });
};

const respond = (answer) => {
  const r = resolver;
  resolver = null;
  if (listener) listener({ open: false });
  if (r) r(answer);
};

export const confirmDialog = (opts) => open(opts);

const DEFAULTS = {
  title: "Are you sure?",
  message: "",
  confirmLabel: "Confirm",
  cancelLabel:  "Cancel",
  danger: false,
};

export const ConfirmDialogHost = () => {
  const [state, setState] = useState({ open: false, ...DEFAULTS });

  useEffect(() => {
    listener = (s) => setState((prev) => ({ ...prev, ...DEFAULTS, ...s }));
    return () => { listener = null; };
  }, []);

  // Close on Escape.
  useEffect(() => {
    if (!state.open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") respond(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.open]);

  if (!state.open) return null;
  const accent = state.danger ? C.red : C.green;

  return (
    <div onClick={() => respond(false)}
      role="dialog" aria-modal="true" aria-label={state.title}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 400, padding: 20, animation: "fadeUp 0.18s ease",
      }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{
          background: C.s1, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: "22px 24px", maxWidth: 440, width: "100%",
          boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
          fontFamily: "'Instrument Sans',sans-serif",
        }}>
        <h3 style={{
          fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800,
          color: "#fff", marginBottom: 8, lineHeight: 1.3,
        }}>{state.title}</h3>
        {state.message && (
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 18 }}>
            {state.message}
          </p>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button onClick={() => respond(false)}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 9, color: C.muted,
              padding: "9px 18px", fontSize: 12, fontWeight: 600, cursor: "pointer",
              fontFamily: "'DM Mono',monospace",
            }}>
            {state.cancelLabel}
          </button>
          <button onClick={() => respond(true)} autoFocus
            style={{
              background: accent, color: "#000", border: "none", borderRadius: 9,
              padding: "9px 22px", fontSize: 12, fontWeight: 700, cursor: "pointer",
              fontFamily: "'DM Mono',monospace",
            }}>
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
